// src/pages/AbschlussBericht.jsx
// Abschluss-Sicht im Protokoll: Bewertung, Sicherheits-Checkboxen, Unterschrift.
// Auto-Save mit 800ms Debounce (gleiche Logik wie EditableStammdaten).

import { useState, useEffect, useRef } from 'react';
import { supabase, withTimeout, pruefpunkteDesProtokolls } from '../lib/supabase';
import { istSichtbar, istBeantwortet, sicherheitsVorschlag, anzeigeNummern, anzeigeNummer } from '../lib/pruefpunktLogik';
import { anlagenLage, kopfFragenLage } from '../lib/anlagenRegeln';
import { C, Field, TextInput, NumberInput, DateInput, Checkbox, Row, datumAnzeige } from './formui';
import SignaturCanvas from './SignaturCanvas';
import Lieferscheine from './Lieferscheine';
import { downloadVollprotokoll, downloadMaengelListe, downloadKurzbericht, prefetchPdfEngine, ladeProtokollDaten } from '../lib/pdfExport';
import DruckAuswahl from './DruckAuswahl';
import { FotoVorschauFuerPdf } from './Fotos';
import { t, modulName } from '../lib/sprache';
import { useAuth } from '../hooks/useAuth';
import { anzeigeName } from '../lib/personen';
import { fotoZaehler, fotosAbarbeiten } from '../lib/fotoWarteschlange';
import {
  abschlussHolen, abschlussVormerken,
  anzahlFuerProtokoll, abarbeiten, offeneAenderungen,
  beiAenderung as beiWarteschlange, abschlussSpeichern, offeneZeilen,
} from '../lib/warteschlange';

// Was die Sichtbarkeit braucht: Isolierart (02.1 nur SF6/Clean Air) und der
// Netzbetreiber (04.10 nur Netze BW/EnBW). Fehler = unbekannt = nichts ausblenden.
async function kontextFuerSichtbarkeit(stationId, trafos) {
  const kontext = { config: null, vnbName: undefined, lage: undefined };
  if (!stationId) return kontext;
  try {
    const { data, error } = await withTimeout(
      // Ganze Zeile: Bedingungen der Art {"config_wahr": "…"} koennen jedes Feld nutzen.
      supabase.from('station_konfiguration').select('*').eq('station_id', stationId).maybeSingle(),
      10000, 'Konfiguration prüfen');
    // Wie im Editor: noch wartende Konfig-Aenderungen obendrauf (z. B. NSHV nicht vorhanden).
    const wartend = offeneZeilen('konfig').find(p => p.station_id === stationId);
    if (!error) kontext.config = (data || wartend) ? { ...(data || {}), ...(wartend || {}) } : null;
    const { data: st, error: e2 } = await withTimeout(
      supabase.from('stationen').select('vnb_id').eq('id', stationId).maybeSingle(), 10000, 'Station prüfen');
    if (!e2) {
      if (!st?.vnb_id) kontext.vnbName = null;
      else {
        const { data: v, error: e3 } = await withTimeout(
          supabase.from('vnb').select('name').eq('id', st.vnb_id).maybeSingle(), 10000, 'Netzbetreiber prüfen');
        if (!e3) kontext.vnbName = v?.name || null;
      }
    }
    // Lage der Anlage (05.18/05.19 entfallen bei SF6/Clean Air ohne Messfeld, Trafo gesteckt)
    const { data: felder, error: e4 } = await withTimeout(
      supabase.from('station_ms_felder').select('typ_code,active').eq('station_id', stationId), 10000, 'MS-Felder prüfen');
    if (!e4 && kontext.config && Array.isArray(trafos)) kontext.lage = anlagenLage(kontext.config, felder || [], trafos);
  } catch { /* unbekannt */ }
  return kontext;
}
// Kapitel-Trafo je Pruefpunkt (Kapitel 04: bezugs_id = Trafo-ID).
function kontextJePunkt(kontext, p, trafos) {
  const bezug = p.protokoll_module?.bezugs_id;
  return { ...kontext, trafo: bezug && Array.isArray(trafos) ? trafos.find(t => t.id === bezug) : undefined };
}


// Die vier Kopfzeilen des Papierprotokolls. Bewusst ohne Vorbelegung: eine
// Voreinstellung waere im Zweifel falsch und stuende trotzdem beim Kunden.
const JA_NEIN_NB = [
  { wert: 'ja', text: 'ja' },
  { wert: 'nein', text: 'nein' },
  { wert: 'n_benoetigt', text: 'n. benötigt' },
];
const JA_NEIN = [
  { wert: 'ja', text: 'ja' },
  { wert: 'nein', text: 'nein' },
];
// Oelprobe: "nicht moeglich" mit Grund statt "n. benoetigt" (Anna-Lena 18.09.2026,
// Migration 58). Ein alter Wert n_benoetigt bleibt sichtbar (alt), wird nicht mehr angeboten.
const OELPROBE = [
  { wert: 'ja', text: 'ja' },
  { wert: 'nein', text: 'nein' },
  { wert: 'nicht_moeglich', text: 'nicht möglich' },
  { wert: 'n_benoetigt', text: 'n. benötigt', alt: true },
];
// entfaellt(lage): Frage passt nicht zur Anlage - ausser es steht schon "ja" drin.
const KOPF_FRAGEN = [
  { feld: 'ist_oelprobe_entnommen',    text: 'Ölprobe entnommen',        optionen: OELPROBE, entfaellt: l => l.nurGh },
  { feld: 'ist_schutzpruefung',        text: 'Schutzprüfung durchgeführt', optionen: JA_NEIN_NB, entfaellt: l => !l.hatLs },
  { feld: 'ist_aggregat_erforderlich', text: 'Aggregat erforderlich für Wartungsmaschinen', optionen: JA_NEIN },
  { feld: 'ist_aggregat_notstrom',     text: 'Aggregat als Notstrom für das Kundennetz', optionen: JA_NEIN },
  { feld: 'ist_pruefplakette',         text: 'Prüfplakette angebracht',  optionen: JA_NEIN },
];

const ZYKLUS_OPTIONEN = [1, 2, 3, 4];
// Wie oft bis zur naechsten faelligen Wartung eine Sichtpruefung stattfinden soll.
const SICHTPRUEFUNGEN = [
  { wert: 1, text: 'einmal' },
  { wert: 2, text: 'zweimal' },
  { wert: 3, text: 'dreimal' },     // Anna-Lena 21.09.2026 (Migration 63)
];
const GESAMTZUSTAND = ['++', '+', '0', '-', '--'];

const ZUSTAND_FARBE = {
  '++': { bg: '#1A7A4A', fg: '#FFFFFF' },
  '+':  { bg: '#5BA476', fg: '#FFFFFF' },
  '0':  { bg: '#9CA3B5', fg: '#FFFFFF' },
  '-':  { bg: '#D97706', fg: '#FFFFFF' },
  '--': { bg: '#C0392B', fg: '#FFFFFF' },
};

// Formularfelder aus einem Protokoll-Datensatz (plus Wartendem) ablesen.
// Ortszeit, nicht UTC: Zwischen 0 und 2 Uhr stand sonst das Datum von gestern da.
const heuteIso = () => {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
};
function formularAus(q) {
  return {
    bewertung_wartungszyklus_jahre: q.bewertung_wartungszyklus_jahre ?? null,
    empfohlene_sichtpruefungen:     q.empfohlene_sichtpruefungen ?? null,
    bewertung_gesamtzustand:        q.bewertung_gesamtzustand ?? null,
    sicherheit_personen_n_g:        !!q.sicherheit_personen_n_g,
    sicherheit_anlagen_n_g:         !!q.sicherheit_anlagen_n_g,
    sicherheit_betrieb_n_g:         !!q.sicherheit_betrieb_n_g,
    sicherheit_netz_n_g:            !!q.sicherheit_netz_n_g,
    dringender_handlungsbedarf:     !!q.dringender_handlungsbedarf,
    handlungsbedarf:                !!q.handlungsbedarf,
    abschluss_bemerkung:            q.abschluss_bemerkung ?? '',
    ist_oelprobe_entnommen:         q.ist_oelprobe_entnommen ?? null,
    ist_schutzpruefung:             q.ist_schutzpruefung ?? null,
    ist_aggregat_erforderlich:      q.ist_aggregat_erforderlich ?? null,
    ist_aggregat_notstrom:          q.ist_aggregat_notstrom ?? null,
    aggregat_notstrom_kva:          q.aggregat_notstrom_kva ?? null,
    oelprobe_grund:                 q.oelprobe_grund ?? '',
    ist_pruefplakette:              q.ist_pruefplakette ?? null,
    monteure:                       q.monteure ?? '',
    monteur_unterschrift_data:      q.monteur_unterschrift_data ?? null,
    monteur_unterschrift_name:      q.monteur_unterschrift_name ?? '',
    monteur_unterschrift_ort:       q.monteur_unterschrift_ort ?? '',
    // Leer wird zu "heute", nie zu '' (die Datenbank-Spalte ist ein Datum).
    monteur_unterschrift_datum:     q.monteur_unterschrift_datum || heuteIso(),
  };
}
// Gleichwertig im Sinne des Formulars (null, '' und false zaehlen als leer).
const leerGleich = (a, b) => JSON.stringify(a === '' || a === false ? null : (a ?? null))
  === JSON.stringify(b === '' || b === false ? null : (b ?? null));

// isLocked: Monteur nach der Abgabe. unterschriftGesperrt: nach der Abgabe
// auch fuers Buero - die Unterschrift gehoert dem Monteur. Alles andere (z. B.
// ein Rechtschreibfehler in der Bemerkung) darf das Buero seit v68 dauerhaft
// korrigieren (Anna-Lena, 17.09.2026).
export default function AbschlussBericht({ protokoll, isLocked, unterschriftGesperrt, onProtokollUpdated }) {
  const { isBuero, profile, user } = useAuth();
  // Vorschlag 4 (15.09.2026): Name, "durchgefuehrt von" und Ort sind beim
  // Monteur schon eingetragen - aendern kann er sie jederzeit. Nur leere
  // Felder, und nicht beim Buero (das schaut nur hinein).
  const vorbelegt = useRef(new Set());
  // Was noch auf dem Geraet wartet, ist NEUER als der Server-Stand. Vorher
  // zeigte das Formular den alten Stand - und der Abschluss schickte ihn
  // zurueck: Ein offline gesetztes Haekchen war danach wieder weg.
  const [data, setData] = useState(() => {
    const f = formularAus({ ...(protokoll || {}), ...abschlussHolen(protokoll?.id) });
    if (!isBuero && !isLocked) {
      const name = anzeigeName(profile, user);
      const ort = protokoll?.stationen?.standort_ort;
      const vorschlag = { monteur_unterschrift_name: name, monteure: name, monteur_unterschrift_ort: ort };
      // Zyklus-Vorschlag nur bei der Wartung - die Sichtpruefung empfiehlt keinen Zyklus.
      if (protokoll?.art !== 'sichtpruefung') {
        vorschlag.bewertung_wartungszyklus_jahre = 2;
        vorschlag.empfohlene_sichtpruefungen = 1;
      }
      for (const [k, v] of Object.entries(vorschlag)) {
        if (v && v !== 'Kollege' && !String(f[k] ?? '').trim()) { f[k] = v; vorbelegt.current.add(k); }
      }
    }
    return f;
  });

  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [abschlussLoading, setAbschlussLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(null); // 'kurz' | 'voll' | 'maengel' | null
  // Offenes Fenster "Was soll gedruckt werden?" (nur Buero): { art, kapitel }
  const [druckWahl, setDruckWahl] = useState(null);
  const timer = useRef(null);
  const timerRest = useRef(0);      // wann der laufende Zeitgeber ablaeuft
  // Nur die Felder, die sich seit dem letzten Speichern geaendert haben.
  // Vorher ging bei JEDEM Tastendruck der komplette Datensatz samt Unterschrift
  // hoch (~900x450-PNG als Base64). Im Feld mit LTE lief das in die Zeitgrenze.
  const dirty = useRef({});
  const [wartetAufNetz, setWartetAufNetz] = useState(false);
  // Felder, die in dieser Sitzung bearbeitet wurden oder beim Oeffnen noch auf
  // dem Geraet warteten. Die ueberschreibt ein Protokoll-Stand von aussen nie
  // (der kann aelter sein, z. B. wenn die Warteschlange im Hintergrund sendet).
  const bearbeitet = useRef(new Set(Object.keys(abschlussHolen(protokoll?.id))));

  // Vorschlag fuer die Sicherheits-Bewertung, abgeleitet aus den Maengeln
  // (Regeln von Anna-Lena, siehe lib/pruefpunktLogik.js).
  const [vorschlag, setVorschlag] = useState(null);

  // Welche Kopf-Fragen zur Anlage passen (Leistungsschalter? nur GH-Trafos?).
  // Ohne Netz / bei Fehlern bleibt alles sichtbar.
  const [kopfLage, setKopfLage] = useState({ hatLs: true, nurGh: false });
  useEffect(() => {
    const sid = protokoll?.station_id;
    if (!sid) return;
    let abgebrochen = false;
    (async () => {
      try {
        const [c, f, tr] = await Promise.all([
          withTimeout(supabase.from('station_konfiguration').select('ms_anlage_vorhanden').eq('station_id', sid).maybeSingle(), 10000, 'Konfiguration prüfen'),
          withTimeout(supabase.from('station_ms_felder').select('typ_code,active').eq('station_id', sid), 10000, 'MS-Felder prüfen'),
          withTimeout(supabase.from('station_trafos').select('trafoart,active').eq('station_id', sid), 10000, 'Trafos prüfen'),
        ]);
        if (abgebrochen) return;
        setKopfLage(kopfFragenLage(c.error ? null : c.data, f.error ? null : f.data, tr.error ? null : tr.data));
      } catch { /* unbekannt - alles zeigen */ }
    })();
    return () => { abgebrochen = true; };
  }, [protokoll?.station_id]);
  const [vorschlagUebernommen, setVorschlagUebernommen] = useState(false);

  useEffect(() => {
    if (!protokoll?.id || isLocked) return;
    let abgebrochen = false;
    (async () => {
      try {
        // Frueher per protokoll_id - die hat die Pruefpunkt-Tabelle nicht.
        // Die Abfrage fand nie etwas, der Vorschlag erschien deshalb nie.
        const data = await pruefpunkteDesProtokolls(
          protokoll.id, 'id,pos_nummer,in_ordnung,wert_auswahl,wert_modifiers,sichtbar_wenn,protokoll_modul_id,protokoll_module(bezugs_id,modul_vorlagen(code))');
        // Antworten, die noch auf dem Geraet warten, zaehlen mit; ausgeblendete
        // Folgefragen nicht (gleiche Regeln wie Lueckencheck und PDF).
        const lokal = offeneAenderungen();
        const punkte = (data || []).map(p => (lokal[p.id] ? { ...p, ...lokal[p.id] } : p));
        let trafos = null;   // unbekannt = nichts ausblenden
        if (protokoll.station_id) {
          const { data: tData, error: tErr } = await withTimeout(
            supabase.from('station_trafos').select('id,trafoart,os_anschluss,active').eq('station_id', protokoll.station_id), 10000, 'Trafos prüfen');
          if (!tErr) trafos = tData || [];
        }
        const kontext = await kontextFuerSichtbarkeit(protokoll.station_id, trafos);
        const sichtbar = punkte.filter(p =>
          istSichtbar(p, punkte.filter(x => x.protokoll_modul_id === p.protokoll_modul_id), trafos, kontextJePunkt(kontext, p, trafos)));
        if (!abgebrochen) setVorschlag(sicherheitsVorschlag(sichtbar));
      } catch {
        // Ohne Vorschlag laesst sich trotzdem arbeiten - kein Grund zu stoeren.
      }
    })();
    return () => { abgebrochen = true; };
  }, [protokoll?.id, protokoll?.station_id, isLocked]);

  // PDF-Engine vorab laden, damit der Export auch offline funktioniert.
  useEffect(() => { prefetchPdfEngine(); }, []);

  // Kundendokumente: Das Buero waehlt vorher, was gedruckt werden soll
  // (Anna-Lena 14.10.2026). Monteure und die Maengelliste gehen direkt durch.
  const pdfErzeugen = async (art) => {
    if (art === 'kurz') await downloadKurzbericht(protokoll.id);
    else if (art === 'voll') await downloadVollprotokoll(protokoll.id);
    else await downloadMaengelListe(protokoll.id);
  };

  const handlePdf = async (art) => {
    if (!protokoll?.id) return;
    if (isBuero && (art === 'kurz' || art === 'voll')) {
      setPdfBusy(art);
      try {
        const daten = await ladeProtokollDaten(protokoll.id);
        const kapitel = (daten.module || [])
          .filter(m => (m.pruefpunkte || []).length > 0)
          .map(m => ({
            id: m.id,
            name: [modulName(m.modul_vorlagen?.code, m.modul_vorlagen?.name) || m.modul_vorlagen?.code,
                   m.bezugs_label].filter(Boolean).join(' · '),
            punkte: m.pruefpunkte,
          }));
        setDruckWahl({ art, kapitel });
      } catch (e) {
        console.error('[PDF]', e);
        alert(t('PDF konnte nicht erstellt werden: {fehler}', { fehler: e.message || t('Unbekannter Fehler') }));
      } finally {
        setPdfBusy(null);
      }
      return;
    }
    setPdfBusy(art);
    try {
      await pdfErzeugen(art);
    } catch (e) {
      console.error('[PDF]', e);
      alert(String(e?.message || '').startsWith('PDF nicht erstellt')
        ? e.message
        : t('PDF konnte nicht erstellt werden: {fehler}', { fehler: e.message || t('Unbekannter Fehler') }));
    } finally {
      setPdfBusy(null);
    }
  };

  // Kommt ein frischerer Stand des Protokolls (erst Zwischenspeicher, dann
  // Server), wird er uebernommen - AUSSER bei Feldern, die hier gerade getippt
  // werden oder noch auf dem Geraet warten. Vorher uebernahm das Formular nur
  // den allerersten Stand, und "Abschliessen" schickte ihn komplett zurueck
  // (v57). Umgekehrt ueberschrieb der Server-Stand eine offline gesetzte
  // Unterschrift.
  useEffect(() => {
    if (!protokoll) return;
    const wartend = abschlussHolen(protokoll.id);
    const frisch = formularAus({ ...protokoll, ...wartend });
    setData(d => {
      let neu = null;
      for (const k of Object.keys(d)) {
        if (bearbeitet.current.has(k) || k in dirty.current || k in wartend || !(k in protokoll)) continue;
        if (vorbelegt.current.has(k) && !String(frisch[k] ?? '').trim()) continue;
        if (!leerGleich(d[k], frisch[k])) { neu = neu || { ...d }; neu[k] = frisch[k]; }
      }
      return neu || d;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protokoll]);

  // Nicht gesendete Aenderungen ueberleben Neuladen und Verbindungsabbruch.
  // Seit v52 in der gemeinsamen Warteschlange: Dort werden sie auch
  // nachgesendet, wenn dieser Reiter NICHT offen ist (vorher: praktisch nie).
  // In einem Ref, weil der Hoerer fuer "etb-jetzt-speichern" nur EINMAL registriert
  // wird: sonst galt dort noch der Status von damals, und eine Buero-Korrektur
  // wurde nach der Freigabe abgelehnt (Pruefung 20.09.2026).
  const bueroKorrekturRef = useRef(false);
  bueroKorrekturRef.current = isBuero && ['abgeschlossen', 'monteur_abgeschlossen', 'buero_freigegeben'].includes(protokoll?.status);
  const bueroKorrektur = () => bueroKorrekturRef.current;
  const merkeWartend = (patch) => abschlussVormerken(protokoll?.id, patch, bueroKorrektur());
  const holeWartend = () => abschlussHolen(protokoll?.id);
  // Gesendet wird seit v54 ausschliesslich ueber abschlussSpeichern (feste
  // Reihenfolge) - der alte Direktweg sendePatch ist entfallen.

  const speichereWartende = async () => {
    if (Object.keys(holeWartend()).length === 0) return;
    try {
      // abschlussSenden schuetzt davor, dass ein alter Stand eine inzwischen
      // erfolgte Buero-Freigabe wieder zuruecksetzt. Ueber abschlussSpeichern
      // in derselben Reihenfolge wie alles andere.
      const r = await abschlussSpeichern(protokoll.id, {});
      if (r.status === 'gesendet') {
        setWartetAufNetz(false);
        setStatus('saved');
        setError(null);
        setTimeout(() => setStatus('idle'), 1500);
      } else {
        setWartetAufNetz(true);
      }
    } catch (e) {
      setWartetAufNetz(false);
      setError(t('Von der Datenbank abgelehnt: {fehler}', { fehler: e.message || e }));
      setStatus('error');
    }
  };

  // Beim Oeffnen und sobald wieder Netz da ist: Liegengebliebenes nachsenden.
  useEffect(() => {
    if (!protokoll?.id) return;
    if (Object.keys(holeWartend()).length > 0) { setWartetAufNetz(true); speichereWartende(); }
    const beiNetz = () => speichereWartende();
    window.addEventListener('online', beiNetz);
    return () => window.removeEventListener('online', beiNetz);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protokoll?.id]);

  // verzoegerung: wie lange nach der letzten Eingabe gewartet wird.
  // Die Unterschrift meldet sich nach JEDEM Federstrich. Mit 800 ms wuerde ein
  // langsam Schreibender sie mehrfach hochladen. Deshalb bekommt sie 2,5 s -
  // im Bildschirm steht sie sofort, gesendet wird sie einmal am Ende.
  const update = (patch, verzoegerung = 800) => {
    if (isLocked) return;
    Object.keys(patch).forEach(k => bearbeitet.current.add(k));
    setData(d => ({ ...d, ...patch }));
    dirty.current = { ...dirty.current, ...patch };
    // Ein gemeinsamer Zeitgeber hat die 2,5 s der Unterschrift auf 0,8 s verkuerzt,
    // sobald danach ein Buchstabe kam - dann ging das grosse Bild mehrfach raus.
    // Deshalb: die laengere Wartezeit gewinnt (Pruefung 20.09.2026).
    if (timer.current) {
      clearTimeout(timer.current);
      verzoegerung = Math.max(verzoegerung, timerRest.current - Date.now());
    }
    timerRest.current = Date.now() + verzoegerung;
    timer.current = setTimeout(async () => {
      const zuSenden = dirty.current;
      if (Object.keys(zuSenden).length === 0) return;
      dirty.current = {};
      setStatus('saving'); setError(null);
      try {
        // Erst gemerkt, dann in fester Reihenfolge gesendet (warteschlange.js).
        const r = await abschlussSpeichern(protokoll.id, zuSenden, bueroKorrektur());
        if (r.status === 'wartet') {
          setWartetAufNetz(true);
          setStatus('idle');
        } else {
          setStatus('saved');
          if (onProtokollUpdated) onProtokollUpdated(zuSenden);
          setTimeout(() => setStatus(st => (st === 'saved' ? 'idle' : st)), 1500);
        }
      } catch (e) {
        // Echte Ablehnung (Rechte, Pruefregel): sichtbar machen, statt "kein
        // Netz" vorzutaeuschen. Die Eingabe bleibt fuers naechste Speichern.
        dirty.current = { ...zuSenden, ...dirty.current };
        setError(t('Nicht gespeichert: {fehler}', { fehler: e.message || t('Datenbank lehnt ab') }));
        setStatus('error');
      }
    }, verzoegerung);
  };

  // Speichern-Knopf / App in den Hintergrund: Was noch 0,8 s (Unterschrift
  // 2,5 s) wartet, SOFORT ins Geraet. Die Warteschlange sendet es dann.
  useEffect(() => {
    const sofort = () => {
      if (!timer.current) return;
      clearTimeout(timer.current);
      timer.current = null;
      const zuSenden = dirty.current;
      if (Object.keys(zuSenden).length === 0) return;
      dirty.current = {};
      merkeWartend(zuSenden);
      setWartetAufNetz(true);
    };
    window.addEventListener('etb-jetzt-speichern', sofort);
    // Hinweis "auf dem Geraet gespeichert" verschwindet, sobald gesendet ist.
    const abmelden = beiWarteschlange(() => {
      if (protokoll?.id) setWartetAufNetz(Object.keys(abschlussHolen(protokoll.id)).length > 0);
    });
    return () => { window.removeEventListener('etb-jetzt-speichern', sofort); abmelden(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protokoll?.id]);

  const handleAbschluss = async () => {
    if (abschlussLoading) return;   // Doppeltipp
    if (!data.monteur_unterschrift_name?.trim()) {
      alert(t('Bitte zuerst deinen Namen eintragen.'));
      return;
    }
    if (!data.monteur_unterschrift_data) {
      alert(t('Bitte zuerst unterschreiben.'));
      return;
    }
    setAbschlussLoading(true);
    try {
      // Antworten, die noch auf dem Geraet liegen, zuerst losschicken.
      if (anzahlFuerProtokoll(protokoll.id) > 0) {
        // Hoechstens 20 s warten - nach viel Offline-Arbeit liefe das sonst
        // minutenlang. Was nicht rausging, bleibt gemerkt.
        try { await Promise.race([abarbeiten(), new Promise(r => setTimeout(r, 20000))]); } catch { /* bleibt gemerkt */ }
      }
      const nochAufGeraet = anzahlFuerProtokoll(protokoll.id);
      // Fotos, die noch im Geraet liegen: erst versuchen, dann ehrlich fragen.
      if (navigator.onLine) {
        try { await Promise.race([fotosAbarbeiten(), new Promise(r => setTimeout(r, 20000))]); } catch { /* bleibt gemerkt */ }
      }
      let fotos = null;
      try { fotos = await fotoZaehler(protokoll.id); } catch { fotos = null; }
      if (fotos && fotos.wartend + fotos.abgelehnt > 0 && !window.confirm(
        (fotos.wartend > 0
          ? t('{n} Foto(s) sind noch nicht hochgeladen (kein Netz). Sie gehen automatisch raus, fehlen aber in einem PDF, das vorher erstellt wird.', { n: fotos.wartend })
          : '')
        + (fotos.abgelehnt > 0 ? '\n' + t('{n} Foto(s) wurden vom Server abgelehnt und gehen NICHT von allein raus.', { n: fotos.abgelehnt }) : '')
        + '\n\n' + t('Trotzdem weiter?'))) return;
      if (!fotos && !window.confirm(t('Ob noch Fotos auf dem Gerät warten, ließ sich gerade nicht prüfen.') + '\n\n' + t('Trotzdem weiter?'))) return;

      // 15.05.: Lueckencheck vor Abschluss (#38). Alle Pruefpunkte des Protokolls laden
      // und zaehlen, welche noch nicht beantwortet sind (kein wert_auswahl/zahl/bool/text gesetzt).
      try {
        // Frueher per protokoll_id - die hat die Pruefpunkt-Tabelle nicht.
        const ppData = await pruefpunkteDesProtokolls(protokoll.id,
          'id,bezeichnung,pos_nummer,reihenfolge,wert_auswahl,wert_zahl,wert_bool,wert_text,wert_modifiers,sichtbar_wenn,protokoll_modul_id,protokoll_module(bezugs_id,modul_vorlagen(name,code))');
        // Antworten, die noch auf dem Geraet warten, zaehlen als beantwortet.
        const lokal = offeneAenderungen();
        const punkte = (ppData || []).map(p => (lokal[p.id] ? { ...p, ...lokal[p.id] } : p));
        // Trafos der Station laden - manche Fragen haengen davon ab (z.B. das
        // Warnschild fuer Giessharz-Trafos, die Oelwanne nur bei Oel-Trafos).
        let trafos = [];
        if (protokoll.station_id) {
          const { data: tData, error: tErr } = await withTimeout(
            supabase.from('station_trafos').select('id,trafoart,os_anschluss,active').eq('station_id', protokoll.station_id),
            10000, 'Trafos pruefen'
          );
          if (tErr) throw tErr;
          trafos = tData || [];
        }
        // Nur zaehlen, was dem Monteur ueberhaupt angezeigt wurde - und zwar
        // je KAPITEL wie im Editor. Pos-Nummern wie 05.1 gibt es in zwei
        // Kapiteln; uebers ganze Protokoll gerechnet fielen Folgefragen raus.
        const kontext = await kontextFuerSichtbarkeit(protokoll.station_id, trafos);
        const alleSichtbar = punkte.filter(p =>
          istSichtbar(p, punkte.filter(x => x.protokoll_modul_id === p.protokoll_modul_id), trafos, kontextJePunkt(kontext, p, trafos)));
        const offen = alleSichtbar.filter(p => !istBeantwortet(p));
        // Nummern wie im Editor (Kapitel 02 fortlaufend)
        const nummern = {};
        for (const modId of new Set(alleSichtbar.map(p => p.protokoll_modul_id))) {
          const imModul = alleSichtbar.filter(p => p.protokoll_modul_id === modId);
          Object.assign(nummern, anzeigeNummern(imModul[0]?.protokoll_module?.modul_vorlagen?.code, imModul));
        }
        if (offen.length > 0) {
          // Bis zu 8 Beispiele in der Meldung nennen, mit Modulname.
          const examples = offen.slice(0, 8).map(p => {
            const modName = p.protokoll_module?.modul_vorlagen?.name || t('Modul');
            const pos = p.pos_nummer ? anzeigeNummer(p, nummern) + ' ' : '';
            return '  - [' + modName + '] ' + pos + (p.bezeichnung || '');
          }).join('\n');
          const rest = offen.length > 8 ? '\n  ' + t('... und {n} weitere', { n: offen.length - 8 }) : '';
          const meldung = t('Achtung: {n} Pruefpunkte sind noch nicht beantwortet:', { n: offen.length })
            + '\n\n' + examples + rest + '\n\n'
            + t('Moechtest du die Wartung TROTZDEM abschliessen?') + '\n\n'
            + t('(Du kannst auch "Abbrechen" und die fehlenden Punkte zuerst ausfuellen.)');
          if (!window.confirm(meldung)) return;
        }
      } catch (e) {
        console.warn('[Abschluss] Lueckencheck fehlgeschlagen:', e);
        if (!window.confirm(t('Ob alle Punkte beantwortet sind, ließ sich gerade nicht prüfen (kein Netz?).') + '\n\n'
          + t('Trotzdem abschließen?'))) return;
      }
      if (nochAufGeraet > 0 && !window.confirm(
        (nochAufGeraet === 1
          ? t('{n} Eingabe ist noch nicht übertragen (kein Netz).', { n: nochAufGeraet })
          : t('{n} Eingaben sind noch nicht übertragen (kein Netz).', { n: nochAufGeraet }))
        + '\n\n'
        + t('Der Abschluss geht automatisch raus, sobald sie übertragen sind. Trotzdem jetzt abschließen?'))) return;
      if (!window.confirm(t('Wartung jetzt abschliessen? Du kannst danach selbst nichts mehr aendern - nur das Buero.'))) return;

      // Alles, was noch nicht draussen ist, plus der Abschluss selbst. Aus dem
      // Formular NUR, was vom bekannten Protokollstand abweicht - nie den
      // ganzen, womoeglich aelteren Stand.
      const geaendert = {};
      for (const [k, v] of Object.entries(data)) {
        if (!leerGleich(v, protokoll?.[k])) geaendert[k] = v;
      }
      const abschlussPatch = {
        ...holeWartend(),
        ...geaendert,
        ...dirty.current,
        status: 'monteur_abgeschlossen',
        vom_monteur_abgeschlossen_am: new Date().toISOString(),
      };
      dirty.current = {};

      // Ein Weg fuer alles: merken, dann in fester Reihenfolge senden. Liegen
      // noch Antworten auf dem Geraet, wartet der Abschluss auf sie - sonst
      // saehe das Buero ein fertiges Protokoll mit Luecken.
      try {
        const r = await abschlussSpeichern(protokoll.id, abschlussPatch);
        if (onProtokollUpdated) onProtokollUpdated(abschlussPatch);
        if (r.status === 'wartet') {
          setWartetAufNetz(true);
          alert(nochAufGeraet > 0
            ? t('Dein Abschluss ist auf dem Gerät gespeichert. Er wird automatisch übertragen, sobald alle Antworten draußen sind.')
              + ' ' + t('Du kannst weiterfahren.')
            : t('Kein oder schlechtes Netz.') + '\n\n'
              + t('Dein Abschluss ist auf dem Gerät gespeichert und wird automatisch übertragen, sobald du wieder Empfang hast.')
              + ' ' + t('Du kannst weiterfahren.'));
        } else {
          setWartetAufNetz(false);
          alert(t('Wartung erfolgreich abgeschlossen. Das Büro kann jetzt prüfen und freigeben.'));
        }
      } catch (e) {
        // Echte Ablehnung: NICHT als "kein Netz" tarnen. Eingaben behalten,
        // aber ohne den Status - der darf nicht beim naechsten Tippen mitgehen.
        const { status: _s, vom_monteur_abgeschlossen_am: _v, ...eingaben } = abschlussPatch;
        dirty.current = { ...eingaben, ...dirty.current };
        alert(t('Der Abschluss wurde von der Datenbank ABGELEHNT:') + '\n\n' + (e.message || e)
          + '\n\n' + t('Bitte das Büro informieren. Deine Eingaben bleiben erhalten.'));
      }
    } finally {
      setAbschlussLoading(false);
    }
  };

  const handleBueroFreigabe = async () => {
    if (!window.confirm(t('Wartungsprotokoll als geprüft und freigegeben markieren?'))) return;
    setAbschlussLoading(true);
    try {
      // getSession() liest die bereits vorhandene Anmeldung aus dem Geraet.
      // Vorher stand hier getUser() - das fragt jedes Mal beim Server nach und
      // war ein zusaetzlicher Netzaufruf mitten in der Freigabe.
      let session = null;
      try {
        const r = await withTimeout(supabase.auth.getSession(), 5000, 'Anmeldung lesen');
        session = r?.data?.session || null;
      } catch { /* dann ohne Namen - die Freigabe selbst zaehlt */ }
      const freigabePatch = {
        status: 'buero_freigegeben',
        vom_buero_freigegeben_am: new Date().toISOString(),
        vom_buero_freigegeben_von: session?.user?.id ?? null,
      };
      // Derselbe Weg wie alles andere (merken, dann in Reihenfolge senden).
      // Echte Ablehnung wirft -> sichtbare Fehlermeldung (aeusseres catch).
      const r = await abschlussSpeichern(protokoll.id, freigabePatch);
      if (onProtokollUpdated) onProtokollUpdated(freigabePatch);
      if (r.status === 'wartet') {
        setWartetAufNetz(true);
        alert(t('Die Freigabe konnte gerade nicht übertragen werden.') + '\n\n'
          + t('Sie ist gespeichert und geht automatisch raus, sobald die Verbindung wieder steht.'));
      } else {
        setWartetAufNetz(false);
        alert(t('Protokoll vom Büro freigegeben.'));
      }
    } catch (e) {
      alert(t('Fehler: {fehler}', { fehler: e.message || t('Unbekannt') }));
    } finally {
      setAbschlussLoading(false);
    }
  };

  const istMonteurFertig = ['abgeschlossen', 'monteur_abgeschlossen', 'buero_freigegeben'].includes(protokoll?.status);
  const istBueroFreigegeben = protokoll?.status === 'buero_freigegeben';

  return (
    <div style={{ background: C.white, borderRadius: 10, padding: 22, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>

      <div style={{ marginBottom: 18, padding: 12, borderRadius: 8,
        background: istBueroFreigegeben ? C.greenBg : istMonteurFertig ? '#FEF3DC' : C.gray100,
        color: istBueroFreigegeben ? C.green : istMonteurFertig ? '#9A6500' : C.gray700,
        fontSize: 15, fontWeight: 700 }}>
        {istBueroFreigegeben && t('OK Vom Büro freigegeben am {datum}', { datum: datumAnzeige(protokoll.vom_buero_freigegeben_am?.substring(0, 10)) || '?' })}
        {!istBueroFreigegeben && istMonteurFertig && t('Vom Monteur abgeschlossen am {datum} - wartet auf Büro-Freigabe', { datum: datumAnzeige(protokoll.vom_monteur_abgeschlossen_am?.substring(0, 10)) || '?' })}
        {!istMonteurFertig && t('Status: Entwurf - bitte unten Bewertung + Unterschrift abgeben.')}
      </div>

      {!isLocked && isBuero && istMonteurFertig && (
        <div style={{ marginBottom: 14, padding: '10px 12px', background: C.orangeBg, borderLeft: '4px solid ' + C.orange,
                      borderRadius: 6, fontSize: 15, color: C.navy }}>
          {t('Büro-Korrektur: Bewertung und Bemerkung lassen sich weiter ändern, die Unterschrift nicht. Ein bereits verschicktes PDF danach neu erstellen.')}
        </div>
      )}

      <FotoVorschauFuerPdf protokollId={protokoll?.id} />

      <div style={{ marginBottom: 18, padding: 14, background: C.gray100, borderRadius: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, marginBottom: 4 }}>{t('PDF herunterladen')}</div>
        <div style={{ fontSize: 13, color: C.gray500, marginBottom: 10 }}>
          {t('Erzeugt ein PDF zum Speichern, Drucken oder Versenden. Braucht Netz — die Daten kommen frisch aus der Datenbank.')}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* Der Kurzbericht steht zuerst: Das ist das, was der Kunde bekommt. */}
          <button type='button' onClick={() => handlePdf('kurz')} disabled={pdfBusy !== null}
            style={{ padding: '10px 18px', background: pdfBusy === 'kurz' ? C.gray300 : C.navy, color: C.white, border: 'none', borderRadius: 8, cursor: pdfBusy ? 'wait' : 'pointer', fontWeight: 800, fontSize: 15, textAlign: 'left', lineHeight: 1.25, opacity: pdfBusy && pdfBusy !== 'kurz' ? 0.5 : 1 }}>
            {pdfBusy === 'kurz' ? t('Erstelle PDF...') : (<>
              ↓ {t('Wartungsbericht')}
              <span style={{ display: 'block', fontSize: 12, fontWeight: 600, opacity: 0.75 }}>{t('für den Kunden')}</span>
            </>)}
          </button>
          <button type='button' onClick={() => handlePdf('voll')} disabled={pdfBusy !== null}
            style={{ padding: '10px 18px', background: pdfBusy === 'voll' ? C.gray300 : C.white, color: C.navy, border: '2px solid ' + C.navy, borderRadius: 8, cursor: pdfBusy ? 'wait' : 'pointer', fontWeight: 800, fontSize: 15, textAlign: 'left', lineHeight: 1.25, opacity: pdfBusy && pdfBusy !== 'voll' ? 0.5 : 1 }}>
            {pdfBusy === 'voll' ? t('Erstelle PDF...') : (<>
              ↓ {t('Vollständiges Protokoll')}
              <span style={{ display: 'block', fontSize: 12, fontWeight: 600, opacity: 0.75 }}>{t('alle Prüfpunkte')}</span>
            </>)}
          </button>
          <button type='button' onClick={() => handlePdf('maengel')} disabled={pdfBusy !== null}
            style={{ padding: '10px 18px', background: pdfBusy === 'maengel' ? C.gray300 : C.white, color: C.navy, border: '2px solid ' + C.navy, borderRadius: 8, cursor: pdfBusy ? 'wait' : 'pointer', fontWeight: 800, fontSize: 15, opacity: pdfBusy && pdfBusy !== 'maengel' ? 0.5 : 1 }}>
            {pdfBusy === 'maengel' ? t('Erstelle PDF...') : '↓ ' + t('Mängelliste (für Angebot)')}
          </button>
        </div>
      </div>

      {/* Die vier Zeilen aus dem Kopf des Papierprotokolls. Sie stehen in der
          Kundenversion ganz oben und haben in der App bisher gefehlt. */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, marginBottom: 4 }}>
          {t('Angaben zur Wartung')}
        </div>
        <div style={{ fontSize: 13, color: C.gray500, marginBottom: 10 }}>
          {t('Diese Angaben stehen oben im Protokoll für den Kunden.')}
        </div>
        {KOPF_FRAGEN.filter(f => !(f.entfaellt && f.entfaellt(kopfLage) && data[f.feld] !== 'ja')).map(f => (
          <div key={f.feld} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 210px', minWidth: 200, fontSize: 15, color: C.navy, fontWeight: 600 }}>
              {t(f.text)}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {f.optionen.filter(o => !o.alt || data[f.feld] === o.wert).map(o => {
                const sel = data[f.feld] === o.wert;
                return (
                  <button key={o.wert} type='button' disabled={isLocked}
                    onClick={() => update({ [f.feld]: sel ? null : o.wert })}
                    style={{ minHeight: 46, padding: '9px 16px', borderRadius: 8,
                             border: '2px solid ' + (sel ? C.navy : C.gray200),
                             background: sel ? C.navy : C.white,
                             color: sel ? C.white : C.gray700,
                             fontWeight: 700, fontSize: 15,
                             cursor: isLocked ? 'default' : 'pointer' }}>
                    {t(o.text)}
                  </button>
                );
              })}
            </div>
          </div>
          {f.feld === 'ist_oelprobe_entnommen' && data.ist_oelprobe_entnommen === 'nicht_moeglich' && (
            <Field label={t('Grund, warum keine Ölprobe möglich war')}>
              <TextInput value={data.oelprobe_grund} onChange={v => update({ oelprobe_grund: v })}
                         placeholder={t('z. B. kein Probenahmeventil, Hermetik-Trafo')}/>
            </Field>
          )}
          {f.feld === 'ist_aggregat_notstrom' && data.ist_aggregat_notstrom === 'ja' && (
            <Field label={t('Benötigte Leistung [kVA]')}>
              <NumberInput value={data.aggregat_notstrom_kva} step='1' onChange={v => update({ aggregat_notstrom_kva: v })}/>
            </Field>
          )}
          </div>
        ))}
        <div style={{ marginTop: 10 }}>
          <Field label={protokoll?.art === 'sichtpruefung' ? t('Sichtprüfung durchgeführt von') : t('Wartung durchgeführt von')}
                 hint={t('Alle, die dabei waren — z. B. „Jelenic, Urban, Gallo"')}>
            <TextInput value={data.monteure} onChange={v => update({ monteure: v })}/>
          </Field>
        </div>
      </div>

      {/* Bei der Sichtpruefung keine Zyklus-Empfehlung - die gehoert zur Wartung. */}
      {protokoll?.art !== 'sichtpruefung' && (
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, marginBottom: 8 }}>
          {t('Empfohlener Wartungszyklus')}
        </div>
        <div style={{ fontSize: 13, color: C.gray500, marginBottom: 8 }}>
          {t('Aufgrund der durchgeführten Wartung und Berücksichtigung aller Faktoren wie Alter, Zustand, Standort etc. empfehlen wir:')}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {ZYKLUS_OPTIONEN.map(j => {
            const sel = data.bewertung_wartungszyklus_jahre === j;
            return (
              <button key={j} type='button' disabled={isLocked} onClick={() => update({ bewertung_wartungszyklus_jahre: sel ? null : j })}
                style={{ padding: '10px 18px', borderRadius: 8, fontSize: 15, fontWeight: 800, cursor: isLocked ? 'not-allowed' : 'pointer',
                  background: sel ? C.navy : C.white, color: sel ? C.yellow : C.gray700,
                  border: '2px solid ' + (sel ? C.navy : C.gray200) }}>
                {t('{j}-jährig', { j })}
              </button>
            );
          })}
        </div>
        {/* Sichtpruefungen bis zur naechsten Wartung (Anna-Lena 19.09.2026, Migration 59).
            Vorbelegt mit "einmal". */}
        <div style={{ fontSize: 13, color: C.gray500, margin: '12px 0 6px' }}>
          {t('Sichtprüfungen bis zur nächsten fälligen Wartung:')}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {SICHTPRUEFUNGEN.map(o => {
            const sel = data.empfohlene_sichtpruefungen === o.wert;
            return (
              <button key={o.wert} type='button' disabled={isLocked}
                onClick={() => update({ empfohlene_sichtpruefungen: sel ? null : o.wert })}
                style={{ minHeight: 46, padding: '9px 16px', borderRadius: 8, fontSize: 15, fontWeight: 700,
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  background: sel ? C.navy : C.white, color: sel ? C.white : C.gray700,
                  border: '2px solid ' + (sel ? C.navy : C.gray200) }}>
                {t(o.text)}
              </button>
            );
          })}
        </div>
      </div>
      )}

      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, marginBottom: 8 }}>
          {t('Gesamtzustand der elektrischen Anlage')}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {GESAMTZUSTAND.map(z => {
            const sel = data.bewertung_gesamtzustand === z;
            const c = ZUSTAND_FARBE[z];
            return (
              <button key={z} type='button' disabled={isLocked} onClick={() => update({ bewertung_gesamtzustand: sel ? null : z })}
                style={{ padding: '12px 22px', borderRadius: 8, fontSize: 18, fontWeight: 800, cursor: isLocked ? 'not-allowed' : 'pointer',
                  background: sel ? c.bg : C.white, color: sel ? c.fg : c.bg,
                  border: '2px solid ' + c.bg, minWidth: 60 }}>
                {z}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: 18, padding: 14, background: C.gray100, borderRadius: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, marginBottom: 10 }}>{t('Sicherheits-Bewertung')}</div>
        <div style={{ fontSize: 13, color: C.gray500, marginBottom: 10 }}>
          {t('Häkchen setzen, wenn ein Punkt NICHT gewährleistet ist:')}
        </div>

        {/* Vorschlag aus den festgestellten Maengeln. Bewusst kein automatisches
            Setzen: Diese vier Aussagen stehen direkt ueber der Unterschrift.
            Die App rechnet, der Mensch bestaetigt. */}
        {vorschlag?.etwasGesetzt && !vorschlagUebernommen && (
          <div style={{ background: C.yellowBg, borderLeft: '4px solid ' + C.yellow, borderRadius: 7,
                        padding: '13px 15px', marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, marginBottom: 6 }}>
              {t('Vorschlag aus den festgestellten Mängeln')}
            </div>
            <ul style={{ margin: '0 0 10px', paddingLeft: 20, fontSize: 14, color: C.gray700, lineHeight: 1.5 }}>
              {vorschlag.gruende.map(g => <li key={g}>{t(g)}</li>)}
            </ul>
            <div style={{ fontSize: 14, color: C.navy, marginBottom: 10, lineHeight: 1.45 }}>
              {t('Daraus folgt: {liste} nicht gewährleistet.', { liste: [
                vorschlag.werte.sicherheit_personen_n_g && t('Personensicherheit'),
                vorschlag.werte.sicherheit_anlagen_n_g && t('Anlagensicherheit'),
                vorschlag.werte.sicherheit_betrieb_n_g && t('Betriebssicherheit'),
                vorschlag.werte.sicherheit_netz_n_g && t('Netzsicherheit'),
              ].filter(Boolean).join(', ') })}
            </div>
            <button type="button" onClick={() => { update(vorschlag.werte); setVorschlagUebernommen(true); }}
              disabled={isLocked}
              style={{ minHeight: 44, padding: '10px 16px', border: 'none', borderRadius: 8,
                       background: C.navy, color: C.yellow, fontWeight: 800, fontSize: 14,
                       cursor: isLocked ? 'default' : 'pointer' }}>
              {t('Vorschlag übernehmen')}
            </button>
            <button type="button" onClick={() => setVorschlagUebernommen(true)}
              style={{ minHeight: 44, marginLeft: 8, padding: '10px 16px', borderRadius: 8,
                       background: 'none', color: C.gray700, border: '1.5px solid ' + C.gray200,
                       fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              {t('Selbst entscheiden')}
            </button>
          </div>
        )}
        <Row columns={2}>
          <Checkbox checked={data.sicherheit_personen_n_g} onChange={v => update({ sicherheit_personen_n_g: v })} label={t('Personensicherheit nicht gewährleistet')}/>
          <Checkbox checked={data.sicherheit_anlagen_n_g}  onChange={v => update({ sicherheit_anlagen_n_g: v })}  label={t('Anlagensicherheit nicht gewährleistet')}/>
        </Row>
        <Row columns={2}>
          <Checkbox checked={data.sicherheit_betrieb_n_g} onChange={v => update({ sicherheit_betrieb_n_g: v })} label={t('Betriebssicherheit nicht gewährleistet')}/>
          <Checkbox checked={data.sicherheit_netz_n_g}    onChange={v => update({ sicherheit_netz_n_g: v })}    label={t('Netzsicherheit nicht gewährleistet')}/>
        </Row>
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid ' + C.gray200 }}>
          {/* Zwei Stufen (Anna-Lena 21.09.2026, Migration 65) - es gilt immer nur eine. */}
          <Checkbox checked={data.dringender_handlungsbedarf}
            onChange={v => update(v ? { dringender_handlungsbedarf: true, handlungsbedarf: false } : { dringender_handlungsbedarf: false })}
            label={t('Zur Mängelbeseitigung besteht dringender Handlungsbedarf!')}/>
          <div style={{ height: 6 }}/>
          <Checkbox checked={data.handlungsbedarf}
            onChange={v => update(v ? { handlungsbedarf: true, dringender_handlungsbedarf: false } : { handlungsbedarf: false })}
            label={t('Zur Mängelbeseitigung besteht Handlungsbedarf')}/>
        </div>
      </div>

      {/* Lieferscheine dieser Wartung (Migration 66): Monteur laesst den Kunden
          unterschreiben, das Buero kann hier auch hochladen. */}
      <Lieferscheine protokollId={protokoll?.id} stationId={protokoll?.station_id}
        darfHochladen={isBuero} darfUnterschreiben={!isLocked} userId={user?.id}/>

      <div style={{ marginBottom: 18 }}>
        <Field label={t('Abschluss-Bemerkung (optional)')} hint={t('z.B. besondere Hinweise, Empfehlungen, Termine')}>
          <TextInput value={data.abschluss_bemerkung} onChange={v => update({ abschluss_bemerkung: v })}/>
        </Field>
      </div>

      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, marginBottom: 8 }}>{t('Unterschrift Monteur')}</div>
        <Row columns={3}>
          <Field label={t('Ort')}><TextInput value={data.monteur_unterschrift_ort} onChange={v => update({ monteur_unterschrift_ort: v })}/></Field>
          <Field label={t('Datum')}><DateInput value={data.monteur_unterschrift_datum} onChange={v => update({ monteur_unterschrift_datum: v || null })}/></Field>
          <Field label={t('Name (gedruckt)')}><TextInput value={data.monteur_unterschrift_name} onChange={v => update({ monteur_unterschrift_name: v })}/></Field>
        </Row>
        <div style={{ fontSize: 13, color: C.gray500, marginBottom: 6, marginTop: 8 }}>{t('Unterschrift')}</div>
        <SignaturCanvas
          value={data.monteur_unterschrift_data}
          onChange={(d) => update({ monteur_unterschrift_data: d }, 2500)}
          disabled={isLocked || unterschriftGesperrt}
        />
      </div>

      {/* Wartet noch etwas auf Netz? Das ist keine Fehlermeldung, sondern ein
          ruhiger Hinweis - der Monteur muss nichts tun und nichts geht verloren. */}
      {wartetAufNetz && (
        <div style={{ marginBottom: 10, padding: '10px 14px', background: C.yellowBg, borderLeft: '4px solid ' + C.yellow,
                      borderRadius: 6, fontSize: 15, color: C.navy, lineHeight: 1.5 }}>
          <strong>{t('Auf dem Gerät gespeichert.')}</strong>{' '}
          {t('Die Übertragung läuft automatisch, sobald du wieder Empfang hast.')}{' '}
          {t('Du kannst die App schließen — es geht nichts verloren.')}
          <button type='button' onClick={speichereWartende}
            style={{ marginLeft: 10, padding: '4px 12px', border: '1px solid ' + C.navy, borderRadius: 6,
                     background: C.white, color: C.navy, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
            {t('Jetzt versuchen')}
          </button>
        </div>
      )}

      <div style={{ minHeight: 20, marginBottom: 8, fontSize: 14, color: status === 'error' ? C.red : C.gray500, fontWeight: 700 }}>
        {status === 'saving' && t('speichert...')}
        {status === 'saved' && '✓ ' + t('gespeichert')}
        {status === 'error' && '! ' + error}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14, paddingTop: 14, borderTop: '2px solid ' + C.gray100 }}>
        {/* Der wichtigste Knopf der Wartung: volle Breite, gross (vorher 14 px,
            rechtsbuendig und leicht zu uebersehen). */}
        {!istMonteurFertig && (
          <button onClick={handleAbschluss} disabled={abschlussLoading}
            style={{ width: '100%', minHeight: 56, padding: '12px 22px', background: abschlussLoading ? C.gray300 : C.green, color: C.white, border: 'none', borderRadius: 10, cursor: abschlussLoading ? 'wait' : 'pointer', fontWeight: 800, fontSize: 17, opacity: abschlussLoading ? 0.7 : 1 }}>
            {abschlussLoading ? t('Speichert...') : protokoll?.art === 'sichtpruefung' ? t('Sichtprüfung abschließen (Monteur)') : t('Wartung abschließen (Monteur)')}
          </button>
        )}
        {/* Nur Buero/Admin. Vorher sah auch der Monteur den Knopf und konnte
            sein eigenes Protokoll freigeben. */}
        {istMonteurFertig && !istBueroFreigegeben && isBuero && (
          <button onClick={handleBueroFreigabe} disabled={abschlussLoading}
            style={{ width: '100%', minHeight: 56, padding: '12px 22px', background: abschlussLoading ? C.gray300 : C.green, color: C.white, border: 'none', borderRadius: 10, cursor: abschlussLoading ? 'wait' : 'pointer', fontWeight: 800, fontSize: 17, opacity: abschlussLoading ? 0.7 : 1 }}>
            {abschlussLoading ? t('Speichert...') : t('Vom Büro freigeben')}
          </button>
        )}
      </div>
      {/* Was soll gedruckt werden? Nur Buero, nur Kundendokumente. */}
      {druckWahl && (
        <DruckAuswahl
          kapitel={druckWahl.kapitel}
          titel={druckWahl.art === 'voll' ? t('Vollprotokoll') : t('Kundenbericht')}
          onAbbrechen={() => setDruckWahl(null)}
          onDrucken={async () => {
            const art = druckWahl.art;
            setDruckWahl(null);
            setPdfBusy(art);
            try { await pdfErzeugen(art); }
            catch (e) {
              console.error('[PDF]', e);
              alert(t('PDF konnte nicht erstellt werden: {fehler}', { fehler: e.message || t('Unbekannter Fehler') }));
            } finally { setPdfBusy(null); }
          }}/>
      )}
    </div>
  );
}